import { isEmpty } from 'lodash';

import type { AppService } from 'app/developer-v3/types';
import type { AppDefinitionSchema } from 'app/developer-v3/platformSchema/app-definition';
import type { ZapData } from 'app/entities/Zap/types';
import { CliAppDefinitionData } from 'app/entities/CliAppDefinition/types';

type ZapDetail = {
  hasTask: boolean;
  hasZap: boolean;
  key: string;
  name: string;
  type: 'triggers' | 'creates' | 'searches';
};

const matchZaps = (
  selectedApi: string,
  definition: AppDefinitionSchema,
  zaps: Array<ZapData>,
  types: Array<'triggers' | 'creates' | 'searches'>
) => {
  return types.flatMap(type => {
    const schema = definition[type];

    if (!schema) {
      return [];
    }

    return Object.entries(schema).map(([key, def]: [string, AppService]) => {
      const matchedZap = zaps.find(
        ({ nodes }) =>
          !isEmpty(nodes) &&
          nodes.some(
            node => node.selected_api === selectedApi && node.action === key
          )
      );

      return {
        key,
        name: def.display.label,
        zapId: matchedZap?.id,
        type,
      };
    });
  });
};

/**
 * Gives a summary of an implementation's Zap/Task data
 * (ie. Show whether each trigger/action have a corresponding Zap and/or Task).
 * If @zaps and @tasks aren't included, we still get the trigger/action detail summary
 */
function parseZapDetails(
  appVersion: CliAppDefinitionData,
  zaps: ZapData[] = [],
  zapIdsOfTasks: ID[] = []
): { triggers: ZapDetail[]; actions: ZapDetail[] } {
  const selectedApi = appVersion.selected_api;
  const definition = appVersion.definition_override || {};

  const zapTriggers = matchZaps(selectedApi, definition, zaps, ['triggers']);
  const zapActions = matchZaps(selectedApi, definition, zaps, [
    'creates',
    'searches',
  ]);

  const appendDetails = ({ key, name, type, zapId }) => {
    return {
      hasZap: !!zapId,
      hasTask: !!zapId && zapIdsOfTasks.some(id => id === zapId),
      key,
      name,
      type,
    };
  };

  return {
    triggers: zapTriggers.map(appendDetails),
    actions: zapActions.map(appendDetails),
  };
}

export { parseZapDetails };
